import React from 'react';
import i18next from 'i18next';
import { DateFormatter } from 'components/AppTable/CellsComponents';

const formatStatus = value => i18next.t(`trainingSessions.statuses.${value}`, { defaultValue: value });

const formatDeliveryMethod = value => i18next.t(`trainingSessions.deliveryMethods.${value}`, { defaultValue: value });

const formatTrainingPart = (versionCode, trainingMethod) => {
  if (!versionCode && !trainingMethod) {
    return '—';
  }
  if (!trainingMethod) {
    return versionCode || '—';
  }
  const methodLabel = i18next.t(`trainingModules.tabs.${trainingMethod}`, { defaultValue: trainingMethod });
  return versionCode ? `${versionCode} (${methodLabel})` : `(${methodLabel})`;
};

const formatSessionName = (code, name) => {
  if (!code && !name) {
    return '—';
  }
  if (!code) {
    return name;
  }
  if (!name) {
    return code;
  }
  return `${code}. ${name}`;
};

const formatParticipantsCapacity = (participantCount, maxStudents) => {
  const enrolled = participantCount == null ? 0 : participantCount;
  if (maxStudents == null || maxStudents === '') {
    return `${enrolled} / ${i18next.t('trainingSessions.unlimited')}`;
  }
  return `${enrolled} / ${maxStudents}`;
};

const formatScopeStation = (value, stationName) => {
  if (value === 'global' || stationName == null) {
    return i18next.t('trainingSessions.scopes.global');
  }
  return stationName || i18next.t('trainingSessions.scopes.local');
};

const getTableConfig = ({ forTrainer = false } = {}) => {
  const columns = [
    {
      title: i18next.t('tablesLabels.id'),
      field: 'id',
      columnWidth: '40px',
      isSorting: true,
    },
    {
      title: forTrainer
        ? i18next.t('tablesLabels.trainingModule')
        : i18next.t('tablesLabels.name'),
      field: 'trainingModuleCode',
      dependentField: 'trainingModuleName',
      columnWidth: '220px',
      Component: formatSessionName,
    },
    {
      title: i18next.t('trainingSessions.trainingPart'),
      field: 'trainingPartVersionCode',
      dependentField: 'trainingMethod',
      columnWidth: '160px',
      Component: formatTrainingPart,
    },
    {
      title: i18next.t('trainingSessions.deliveryMethod'),
      field: 'deliveryMethod',
      columnWidth: '120px',
      Component: formatDeliveryMethod,
    },
    {
      title: i18next.t('tablesLabels.status'),
      field: 'status',
      columnWidth: '100px',
      isSorting: true,
      Component: formatStatus,
    },
    {
      title: 'statusCodeHidden',
      field: 'statusCode',
      isVisible: false,
    },
    {
      title: forTrainer
        ? i18next.t('tablesLabels.startDay')
        : i18next.t('formLabels.startDate'),
      field: 'startDate',
      columnWidth: '120px',
      isSorting: true,
      Component: value => (value ? <DateFormatter value={value} /> : '—'),
    },
    {
      title: forTrainer
        ? i18next.t('tablesLabels.endDay')
        : i18next.t('formLabels.endDate'),
      field: 'endDate',
      columnWidth: '120px',
      isSorting: true,
      Component: value => (value ? <DateFormatter value={value} /> : '—'),
    },
  ];

  if (!forTrainer) {
    columns.push({
      title: i18next.t('formLabels.trainers'),
      field: 'trainerName',
      columnWidth: '160px',
      Component: value => value || '—',
    });
  }

  columns.push(
    {
      title: i18next.t('trainingSessions.participants'),
      field: 'participantCount',
      dependentField: 'maxStudents',
      columnWidth: '130px',
      alignment: 'center',
      Component: formatParticipantsCapacity,
    },
    {
      title: i18next.t('trainingSessions.scope'),
      field: 'scope',
      dependentField: 'stationName',
      columnWidth: '160px',
      Component: formatScopeStation,
    },
    {
      title: 'scopeCodeHidden',
      field: 'scopeCode',
      isVisible: false,
    },
    {
      title: 'moodleLmsCourseIdHidden',
      field: 'moodleLmsCourseId',
      isVisible: false,
    },
    {
      title: 'hasMoodleLinkHidden',
      field: 'hasMoodleLink',
      isVisible: false,
    },
  );

  return columns;
};

export default getTableConfig;
